import { CLOUDINARY_PRESET, CLOUDINARY_VIDEO_UPLOAD } from 'utils/cloudinary';
import { post } from 'utils/api';

import type { ApiUploadEvent } from 'types';

const CHUNK_SIZE_MB = 50;
const CHUNK_SIZE_BYTES = CHUNK_SIZE_MB * 1024 * 1024;

type CloudinaryVideoUploadResponse = {
  publicId: string,
  secureUrl: string,
  duration: number
};

async function uploadChunk(
  uploadId: string,
  file: File,
  chunkNumber: number,
  onUploadProgress?: (progressEvent: ApiUploadEvent) => void
): Promise<CloudinaryVideoUploadResponse> {
  // see https://support.cloudinary.com/hc/en-us/articles/208263735-Guidelines-for-self-implementing-chunked-upload-to-Cloudinary
  const sizeTotal = file.size;
  const chunkStart = chunkNumber * CHUNK_SIZE_BYTES;
  const chunkEnd = Math.min(chunkStart + CHUNK_SIZE_BYTES, sizeTotal);
  const hasMultipleChunks = sizeTotal > CHUNK_SIZE_BYTES;
  const chunkData = (hasMultipleChunks)
    ? file.slice(chunkStart, chunkEnd)
    : file;

  const url = CLOUDINARY_VIDEO_UPLOAD;
  const formData = new FormData();

  formData.append('file', chunkData);
  formData.append('upload_preset', CLOUDINARY_PRESET);

  const headers = (hasMultipleChunks)
    ? {
      'X-Unique-Upload-Id': uploadId,
      'Content-Range': `bytes ${chunkStart}-${chunkEnd - 1}/${sizeTotal}`
    }
    : {};

  const { data } = (await post(url, {
    data: formData,
    headers,
    onUploadProgress: (event) => {
      onUploadProgress?.({
        total: sizeTotal,
        loaded: event.loaded + chunkNumber * CHUNK_SIZE_BYTES
      });
    }
  }, {
    suppressAuth: true,
    suppressCollabHeaders: true
  })) as { data: CloudinaryVideoUploadResponse };

  if (hasMultipleChunks && chunkEnd < sizeTotal) {
    return uploadChunk(uploadId, file, chunkNumber + 1, onUploadProgress);
  }

  return data;
}

export function uploadCloudinaryVideo(videoFile: File, onUploadProgress?: (progressEvent: ApiUploadEvent) => void): Promise<CloudinaryVideoUploadResponse> {
  const id = `id${Math.random()}`;

  return uploadChunk(id, videoFile, 0, onUploadProgress);
}
